// Sentry-lite error reporter — posts to <api host>/api/_client-errors from the
// merchant frontend whenever an error boundary catches something
// non-stale-build. Keep this file dependency-free so it can't itself throw
// during an error condition — hence reading the env var directly rather than
// importing the helper from lib/api.
//
// The host used to be the production literal, so sandbox and local errors were
// all reported into production's client_error_logs, mixing test noise into real
// customer data. With no env var configured we send nothing at all: losing an
// error report is strictly better than filing it against the wrong environment.
const API_BASE = (process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/+$/, '')
const ENDPOINT = API_BASE ? `${API_BASE}/_client-errors` : ''

// Soft per-fingerprint cooldown so a noisy render doesn't flood the
// endpoint, but recurring errors STILL get reported on each occurrence
// (after a 60s gap). The previous Set-based once-per-session dedupe was
// the reason `client_error_logs` had only 2 rows in a week despite
// customer complaints — we were hiding the actual frequency.
const sentInSession = new Map()

/**
 * Send a single error report. Fire-and-forget — never awaits, never throws,
 * never blocks UI. The endpoint is throttled server-side and returns 204
 * on any failure so this can't loop.
 *
 * @param {object} payload
 * @param {string} payload.message    - error.message
 * @param {string} [payload.stack]    - error.stack
 * @param {string} [payload.digest]   - Next.js error.digest
 * @param {string} [payload.category] - 'runtime' | 'boundary' | 'unhandled_rejection' | 'chunk_load'
 * @param {string} [payload.severity] - 'error' | 'warning' | 'info'
 * @param {object} [payload.context]  - free-form bag (route, recent actions, etc)
 */
export function reportClientError(payload) {
  try {
    if (typeof window === 'undefined') return

    const message = String(payload?.message || '').slice(0, 1024)
    if (!message) return

    // No API host configured — stay silent rather than guess at one. Reporting
    // to the wrong environment is worse than not reporting.
    if (!ENDPOINT) return

    // Soft dedupe — same fingerprint allowed once per 60s window instead
    // of once per session. The previous "once per session" was hiding
    // the actual frequency of recurring errors, which is the only signal
    // that lets us prioritise the right root cause.
    const stackFirst = String(payload?.stack || '').split('\n')[0] || ''
    const key = message + '||' + stackFirst
    const now = Date.now()
    const lastSeen = sentInSession.get(key) || 0
    if (now - lastSeen < 60_000) return
    sentInSession.set(key, now)

    // Pull org_uuid out of the URL if we're on a /[org_uuid]/... path or
    // out of localStorage if the app stores it there.
    let orgUuid = null
    try {
      const path = window.location.pathname || ''
      const m = path.match(/^\/([0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12})/i)
      if (m) orgUuid = m[1]
      if (!orgUuid) orgUuid = window.localStorage?.getItem('numbris:selected_org_uuid') || null
    } catch { /* ignore */ }

    const body = {
      app: 'merchant',
      build_id: getBuildId(),
      app_version: getAppVersion(),
      category: payload?.category || 'runtime',
      severity: payload?.severity || 'error',
      message,
      stack: String(payload?.stack || '').slice(0, 16000) || undefined,
      digest: payload?.digest ? String(payload.digest).slice(0, 64) : undefined,
      url: window.location.href.slice(0, 1024),
      context: {
        ...(payload?.context || {}),
        ...(orgUuid ? { org_uuid: orgUuid } : {}),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        referrer: document.referrer?.slice(0, 256) || undefined,
      },
    }

    // Triple transport — try each in order until one succeeds.
    //
    // CRITICAL: sendBeacon with `application/json` type triggers a CORS
    // PREFLIGHT in Edge/Chrome ≥ 149. The preflight succeeds but the
    // browser does NOT reliably follow up with the actual POST when the
    // page is unmounting (error boundary firing) — the POST gets queued
    // then dropped. This is why we saw 12+ OPTIONS but 0 POSTs from TLT.
    //
    // Fix: send as `text/plain` so the beacon is a CORS-simple request
    // (no preflight). Laravel auto-parses JSON from text/plain bodies on
    // the backend. Same wire payload, different content-type only.
    const json = JSON.stringify(body)
    let delivered = false

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const blob = new Blob([json], { type: 'text/plain;charset=UTF-8' })
        delivered = navigator.sendBeacon(ENDPOINT, blob)
      } catch { delivered = false }
    }

    if (!delivered && typeof fetch !== 'undefined') {
      try {
        // Fall-back fetch also uses text/plain to avoid the preflight
        // that's been silently dropping requests on Edge.
        fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: json,
          keepalive: true,
          mode: 'cors',
          credentials: 'omit',
        }).then((r) => { if (!r.ok) console.warn('[numbris] client error POST', r.status) })
          .catch((e) => { console.warn('[numbris] client error POST failed', e?.message); imagePixelFallback(body) })
        delivered = true
      } catch { /* fall through */ }
    }

    if (!delivered) imagePixelFallback(body)
  } catch (outer) {
    // Reporter must never throw — the caller is already in an error state.
    // eslint-disable-next-line no-console
    console.warn('[numbris] reportClientError outer throw', outer?.message)
  }
}

// Last-resort transport: encode key fields as a 1×1 image request. Survives
// even when sendBeacon AND fetch are both blocked (some corporate proxies,
// strict CSP, ad-blockers). The server's GET handler short-circuits on
// `?pixel=1` and ingests the query params as a synthetic error event.
function imagePixelFallback(body) {
  try {
    const params = new URLSearchParams({
      pixel: '1',
      app: body.app || 'merchant',
      message: String(body.message || '').slice(0, 256),
      category: body.category || 'runtime',
      url: String(body.url || '').slice(0, 256),
      build_id: body.build_id || '',
    })
    const img = new Image()
    img.src = ENDPOINT + '?' + params.toString()
  } catch { /* nothing left to try */ }
}

function getBuildId() {
  try {
    // Next.js exposes the build ID in a couple of places; the most stable
    // is __NEXT_DATA__.buildId on any rendered page. Some pages may not
    // have it; fall back to a marker so we know the report came from a
    // context where buildId wasn't readable.
    return window.__NEXT_DATA__?.buildId
      || window.next?.router?.buildId
      || null
  } catch { return null }
}

function getAppVersion() {
  // Optional — could be wired from package.json via a build-time env var.
  try { return process.env.NEXT_PUBLIC_APP_VERSION || null } catch { return null }
}

/**
 * Hook the browser's global error + unhandled rejection events. Call
 * once from the dashboard layout (or any always-mounted client component)
 * so we capture errors that fire OUTSIDE of React boundaries too.
 *
 * Also installs a BUILD_ID poller so stale tabs auto-upgrade after a
 * deploy. Without this, a tab loaded against the old build keeps trying
 * to fetch chunks that don't exist on the new server, even after the
 * static-merge fallback wears off.
 */
export function installGlobalReporters() {
  if (typeof window === 'undefined') return
  if (window.__numbris_error_reporters_installed) return
  window.__numbris_error_reporters_installed = true

  window.addEventListener('error', (e) => {
    const err = e?.error
    reportClientError({
      message: err?.message || e?.message || 'window.onerror',
      stack: err?.stack,
      category: detectCategory(err?.message || e?.message),
    })
    // ChunkLoadError that fires at the window level — try a hard reload
    // ourselves since React boundaries may not have a chance to render
    // before the page goes into a loading-spinner stall.
    maybeHardReload(err?.message || e?.message)
  })

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason
    const message = typeof reason === 'string' ? reason : (reason?.message || 'unhandled rejection')
    reportClientError({
      message,
      stack: reason?.stack,
      category: detectCategory(message),
      severity: 'error',
    })
    maybeHardReload(message)
  })

  // Build-ID poller — soft check every 60s + on tab focus.
  startBuildIdPoller()
}

const STALE_RELOAD_KEY = 'numbris:stale-reload-at'

function maybeHardReload(message) {
  const cat = detectCategory(message)
  if (cat !== 'chunk_load' && cat !== 'stale_build') return
  try {
    const last = Number(window.sessionStorage.getItem(STALE_RELOAD_KEY) || 0)
    if (Date.now() - last < 30_000) return // anti-loop
    window.sessionStorage.setItem(STALE_RELOAD_KEY, String(Date.now()))
    // eslint-disable-next-line no-console
    console.warn('[numbris] stale-build/chunk error — reloading', message)
    window.location.reload()
  } catch { /* ignore */ }
}

function getClientBuildId() {
  try { return window.__NEXT_DATA__?.buildId || null } catch { return null }
}

async function checkBuildId() {
  try {
    const clientId = getClientBuildId()
    if (!clientId) return
    // Cache-bust with a timestamp param + Pragma header. Cloudflare was
    // ignoring `cache: 'no-store'` and serving 2-hour-stale BUILD_IDs;
    // the unique URL forces every layer (browser, CF, ISP) to round-trip
    // to origin so the poller actually detects new deploys.
    const url = '/api/build-id?t=' + Date.now()
    const res = await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    })
    if (!res.ok) return
    const { build_id: serverId } = await res.json()
    if (!serverId || serverId === 'unknown') return
    if (serverId !== clientId) {
      // eslint-disable-next-line no-console
      console.warn('[numbris] build mismatch — client=' + clientId + ' server=' + serverId)
      // Don't auto-reload — the cashier might have an unsaved cart or be
      // mid-checkout. Dispatch an event the UpdateAvailableBanner picks up
      // so they get a visible, user-driven "Refresh now" prompt. The
      // banner is sticky-shown until they click Refresh.
      try {
        window.__numbris_latest_build = serverId
        window.dispatchEvent(new CustomEvent('numbris:update-available', { detail: { client: clientId, server: serverId } }))
      } catch { /* */ }
    }
  } catch { /* network blip — ignore */ }
}

function startBuildIdPoller() {
  if (window.__numbris_build_poller) return
  window.__numbris_build_poller = true
  // Initial check after a short delay (let the page settle).
  setTimeout(checkBuildId, 5_000)
  // Periodic every 60s — light load, no DB.
  setInterval(checkBuildId, 60_000)
  // On tab focus — common case: user came back to a long-idle tab.
  window.addEventListener('focus', checkBuildId)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkBuildId()
  })
}

function detectCategory(message) {
  const m = String(message || '').toLowerCase()
  if (m.includes('chunkload') || m.includes('loading chunk') || m.includes('failed to fetch dynamically imported module')) return 'chunk_load'
  if (m.includes('server action')) return 'stale_build'
  return 'runtime'
}
